import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import StepsBar from '../../BookMeeting/StepsBar';
import styles from './styles.module.scss';

import BuildingContact from './steps/BuildingContact';
import ScopeInputs from './steps/ScopeInputs';
import ReviewSubmit from './steps/ReviewSubmit';
import ThankYou from './steps/ThankYou';
import pushFormSubmitEvent from 'utils/push-form-submit-event';
import { HUBSPOT_FORM_ID, HUBSPOT_FORM_URL } from '../config';

const ESTIMATE_GENERATOR_API_URL = process.env.GATSBY_ESTIMATE_GENERATOR_API_URL;

const hint =
  'By providing a telephone number and submitting this form you are consenting to be contacted by SMS text message. Message & data rates may apply. You can reply STOP to opt-out of further messaging.';

const EstimateWizard = ({
  email = '',
  phone = '',
  onDone = () => {},
  displayView = 'instant',
  revenueheroRouterId = null, // eslint-disable-line no-unused-vars -- Revenue Hero disabled
}) => {
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Store data from each step
  const [step1Data, setStep1Data] = useState({
    buildingType: '',
    buildingTypeOther: '',
    firstName: '',
    lastName: '',
    contactEmail: '',
    phone: '',
    buildingAddress: '',
  });

  const [step2Data, setStep2Data] = useState({
    intercoms: 0,
    cardReaders: 0,
    vehicleReaders: 0,
    smartLocks: 0,
    elevatorAccess: false,
    installationNeeded: false,
    constructionType: 'existing',
    camerasNeeded: false,
  });

  const steps = useMemo(
    () => [
      { label: '1', description: 'Scope Inputs' },
      { label: '2', description: 'Building & Contact' },
      { label: '3', description: 'Review & Submit' },
    ],
    []
  );

  const handleStep1Complete = (data) => {
    setStep2Data(data);
    setActiveStep(2);
  };

  const handleStep2Complete = (data) => {
    setStep1Data(data);
    setActiveStep(3);
  };

  const handleBack = () => {
    setShowError(false);
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const submitToHubspot = useCallback(async (values) => {
    const notes = [
      `${values.intercoms} intercoms`,
      `${values.cardReaders} card readers`,
      `${values.vehicleReaders} vehicle readers`,
      // `${values.smartLocks} smart locks`,
      `${values.elevatorAccess ? 'With Elevator Access Control' : ''}`,
      `${values.installationNeeded ? 'Installation Help Needed' : ''}`,
      `${values.constructionType === 'existing' ? 'Existing System' : 'New Construction'}`,
      // `${values.camerasNeeded ? 'Want Security Cameras' : ''}`,
    ]
      .filter(Boolean)
      .join(', ');

    const gclid = localStorage.getItem('gclid')
      ? JSON.parse(localStorage.getItem('gclid')).value
      : '';
    const slcode = localStorage.getItem('slcode')
      ? JSON.parse(localStorage.getItem('slcode')).value
      : '';

    await fetch(HUBSPOT_FORM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: [
          { name: 'firstname', value: values.firstName },
          { name: 'lastname', value: values.lastName },
          { name: 'email', value: values.contactEmail },
          { name: 'phone', value: values.phone },
          { name: 'building_type', value: values.buildingType || values.buildingTypeOther },
          { name: 'building_address', value: values.buildingAddress },
          { name: 'notes__c', value: notes },
          { name: 'slcode', value: slcode },
          { name: 'gclid', value: gclid },
          { name: 'want_security_cameras_', value: values.camerasNeeded },
        ],
        context: {
          pageUri: window.location.href,
          pageName: 'Estimate Generator',
        },
      }),
    }).then((res) => res.json());

    // This wizard posts straight to the HubSpot API rather than embedding a
    // HubSpot form, so it has to report the submission to GTM itself.
    pushFormSubmitEvent({ formId: HUBSPOT_FORM_ID, email: values.contactEmail });
  }, []);

  const generateEstimate = useCallback(async (values) => {
    const {
      firstName,
      lastName,
      contactEmail,
      phone,
      buildingAddress,
      intercoms,
      cardReaders,
      vehicleReaders,
      elevatorAccess,
      installationNeeded,
      camerasNeeded,
    } = values;
    const items = [];

    if (intercoms > 0) items.push({ productId: 'swiftvision-5-inch', quantity: intercoms });
    if (cardReaders > 0) items.push({ productId: 'card-reader', quantity: cardReaders });
    if (vehicleReaders > 0) items.push({ productId: 'vehicle-reader', quantity: vehicleReaders });

    const response = await fetch(`${ESTIMATE_GENERATOR_API_URL}/test-estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer: {
          name: `${firstName} ${lastName}`,
          email: contactEmail,
          phone,
          address: buildingAddress,
        },
        items,
        elevatorAccess,
        installationNeeded,
        camerasNeeded,
      }),
    });

    if (response.ok) {
      // Revenue Hero (disabled): skip auto PDF when router id is set — always download locally
      // if (!revenueheroRouterId) {
      const pdfBlob = await response.blob();
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `swiftlane-estimate-${Date.now()}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      // }
      setShowSuccess(true);
    }
    // Revenue Hero (disabled): always reset contact step after success
    // if (!revenueheroRouterId) {
    setStep1Data({
      buildingType: '',
      buildingTypeOther: '',
      firstName: '',
      lastName: '',
      contactEmail: '',
      phone: '',
      buildingAddress: '',
    });
    // }
    setStep2Data({
      intercoms: 0,
      cardReaders: 0,
      vehicleReaders: 0,
      smartLocks: 0,
      elevatorAccess: false,
      installationNeeded: false,
      camerasNeeded: false,
      constructionType: 'existing',
    });
  }, []);

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    window?.console?.log(values);
    try {
      await submitToHubspot(values);
      await generateEstimate(values);
    } catch (error) {
      window?.console?.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = useCallback(() => {
    if (displayView === 'button') {
      onDone();
    } else {
      setShowSuccess(false);
      setActiveStep(1);
      onDone();
    }
  }, [displayView, onDone]);

  return (
    <div className={styles.wizard}>
      {!showSuccess && (
        <div className={styles.header}>
          <div className={styles.stepsWrapper}>
            <StepsBar steps={steps} currentStep={activeStep} />
          </div>

          <div className={styles.contactInfo}>
            <p>Need help? Contact us:</p>
            <a href={`mailto:${email}`}>{email}</a> | <a href={`tel:${phone}`}>{phone}</a>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {activeStep === 1 && (
          <p className={styles.intro}>Customize your estimate — start by selecting below</p>
        )}
        {showSuccess && (
          <ThankYou
            email={email}
            phone={phone}
            onDone={handleDone}
            revenueheroRouterId={revenueheroRouterId}
            contactData={{
              firstName: step1Data.firstName,
              lastName: step1Data.lastName,
              email: step1Data.contactEmail,
              phone: step1Data.phone,
            }}
          />
        )}
        {!showSuccess && activeStep === 1 && (
          <ScopeInputs initialData={step2Data} onComplete={handleStep1Complete} />
        )}
        {!showSuccess && activeStep === 2 && (
          <BuildingContact
            hint={hint}
            initialData={step1Data}
            onComplete={handleStep2Complete}
            onBack={handleBack}
          />
        )}
        {!showSuccess && activeStep === 3 && (
          <ReviewSubmit
            hint={hint}
            step1Data={step1Data}
            step2Data={step2Data}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onBack={handleBack}
          />
        )}
      </div>
      {showError && <div className={styles.error}>Something went wrong. Please try again.</div>}
    </div>
  );
};

EstimateWizard.propTypes = {
  email: PropTypes.string,
  phone: PropTypes.string,
  onDone: PropTypes.func,
  displayView: PropTypes.oneOf(['instant', 'button']),
  revenueheroRouterId: PropTypes.number,
};

export default EstimateWizard;
